import { IWPBlogCategoryFilter, IWPFeaturedBlogsModuleFull } from "./featured-blogs-module.interface";
import { getWPModuleSpacing } from "@app/api/wp-page-fetcher/utils/get-wp-module-spacing";
import { FeaturedBlogModule } from "@app/api/modules/featured-blog/featured-blog";
import { BlogPost } from "@app/api/core/blog-post/blog-post";
import { moduleMapperThrowMessage } from "@app/api/wp-page-fetcher/utils/module-mapper-error-handling";
import { idsWordPressApiGet, taxonomyWordPressApiGet } from "@app/api/wp-page-fetcher/wordpress-api/base-wp-api";
import { mapWPPostToBlogs } from "./map-featured-blogs";
import { IWPPost, IWordPressPage } from "@app/api/wp-page-fetcher/module-selector/main-wp.interface";
import { IModuleFetcher } from "@app/api/wp-page-fetcher/module-selector/module-selector";
import Logger from "@app/util/logger";

export interface IFeaturedBlogsModuleMapper extends IModuleFetcher {
  module: IWPFeaturedBlogsModuleFull;
}

export const featuredBlogsModuleMapper = async (
  props: IFeaturedBlogsModuleMapper
): Promise<FeaturedBlogModule | undefined> => {
  try {
    const {
      module,
      module: { data },
      platformId,
      pageUrl,
      res
    } = props;
    if (!data) return moduleMapperThrowMessage("No data found in featuredBlogsModuleMapper");

    let featuredBlogs: BlogPost[] = [];
    if (res) {
      const result = await getFeaturedBlogs(
        platformId,
        pageUrl,
        data.showMostRecent,
        Number(data.amountOfItems),
        data.selectBlogs,
        data
      );

      if (!result || result === 404 || result === 503) {
        return moduleMapperThrowMessage("Cannot get featured blogs in featuredBlogsModuleMapper");
      }

      featuredBlogs = result;
    }

    return {
      id: "8",
      name: "FeaturedBlogModule",
      ...getWPModuleSpacing(module.data, module.setMargins),
      background: { backgroundColour: data.bgColor },
      items: featuredBlogs,
      title: data.title || "",
      showMostRecent: data.showMostRecent,
      amountOfItems: Number(data.amountOfItems),
      selectBlogs: data.selectBlogs,
      categoryFilter: { categoryFilter: data.categoryFilter, categories: data.categories }
    };
  } catch (e) {
    Logger.logError(e, "Error in: featuredBlogsModuleMapper");

    return undefined;
  }
};

const DEFAULT_BLOG_TAXONOMY = "blog";
const ALL_POSTS = 0;

export const getFeaturedBlogs = async (
  platformId: number,
  pageUrl?: string,
  showMostRecent?: boolean,
  take?: number,
  selectedBlogs?: IWPPost[],
  categoryFilter?: IWPBlogCategoryFilter
): Promise<BlogPost[] | undefined | (404 | 503)> => {
  if (showMostRecent) {
    const defaultTake = take || 2;
    const allBlogs = await getBlogsByCategoryFilter(platformId, defaultTake + 1, categoryFilter); // +1 so that we have one extra if the blog is removed from the array when it's on the same page
    if (!allBlogs || allBlogs === 404 || allBlogs === 503) return allBlogs;

    const removeCurrentBlog = pageUrl ? allBlogs.filter((blog) => blog.data?.permalink !== pageUrl) : allBlogs;
    const takeBlogs = removeCurrentBlog.slice(0, defaultTake);

    return mapWPPostToBlogs(takeBlogs, platformId);
  }

  if (selectedBlogs && selectedBlogs.length > 0) {
    // Fetched by ID so a selected post outside the blog category (e.g. onderzoek) or older than the newest page still shows
    const allBlogs = await idsWordPressApiGet(
      platformId,
      selectedBlogs.map((blog) => blog.ID)
    );
    if (!allBlogs || allBlogs === 404 || allBlogs === 503) return allBlogs;

    const blogs = getSelectedBlogs(allBlogs, selectedBlogs);
    if (blogs.length === 0) return undefined;

    return mapWPPostToBlogs(blogs, platformId);
  }

  return undefined;
};

const getBlogsByCategoryFilter = async (
  platformId: number,
  take: number,
  categoryFilter?: IWPBlogCategoryFilter
): Promise<IWordPressPage[] | (404 | 503)> => {
  const slugs = getCategorySlugs(categoryFilter);
  const mode = slugs.length > 0 ? categoryFilter?.categoryFilter : "all";

  if (mode === "include") {
    return taxonomyWordPressApiGet(platformId, slugs.join(";"), take);
  }

  if (mode === "exclude") {
    // The wp-api can only include taxonomies, so exclusion filters the full blog list here
    const allBlogs = await taxonomyWordPressApiGet(platformId, DEFAULT_BLOG_TAXONOMY, ALL_POSTS);
    if (!allBlogs || allBlogs === 404 || allBlogs === 503) return allBlogs;

    return allBlogs
      .filter((blog) => !blog.taxonomies?.some((taxonomy) => taxonomy.slug && slugs.includes(taxonomy.slug)))
      .slice(0, take);
  }

  return taxonomyWordPressApiGet(platformId, DEFAULT_BLOG_TAXONOMY, take);
};

const getCategorySlugs = (categoryFilter?: IWPBlogCategoryFilter): string[] => {
  if (!categoryFilter?.categories) return [];

  return categoryFilter.categories.map((category) => category.slug || "").filter((slug) => slug);
};

const getSelectedBlogs = (allBlogs: IWordPressPage[], selectedBlogs: IWPPost[]): IWordPressPage[] | [] => {
  let newBlogs: IWordPressPage[] = [];

  selectedBlogs.forEach((selectedBlog) => {
    const foundBlog = allBlogs.find((blog) => selectedBlog.ID === blog.data?.post?.ID);

    if (foundBlog) newBlogs.push(foundBlog);
  });

  return newBlogs;
};
